import {
    ChangeDetectorRef,
    Directive,
    TemplateRef,
    ViewContainerRef,
    effect,
    inject,
    input,
} from "@angular/core";
import { Observable, Subscription, of } from "rxjs";
import { LoadingComponent } from "./loading.component";

/**
 * Shows a loading spinner until the observable has emitted.
 */
@Directive({
    selector: "[appLoadingUntilFirstEmit]",
})
export class LoadingUntilFirstEmitDirective<T> {
    private templateRef = inject<TemplateRef<{ $implicit: T }>>(TemplateRef);
    private viewContainer = inject(ViewContainerRef);
    private cdr = inject(ChangeDetectorRef);

    private subscription?: Subscription;
    private isLoading = false;

    public readonly appLoadingUntilFirstEmitOf = input.required<T | Observable<T>>();

    public constructor() {
        effect((onCleanUp) => {
            this.renderView();
            onCleanUp(() => this.subscription?.unsubscribe());
        });
    }

    private renderView() {
        // If a second observable is set before the first
        this.subscription?.unsubscribe();

        if (!this.isLoading) {
            this.isLoading = true;
            this.viewContainer.clear();
            this.viewContainer.createComponent(LoadingComponent);
        }

        let value$ = this.appLoadingUntilFirstEmitOf();
        if (!(value$ instanceof Observable)) {
            value$ = of(value$);
        }

        // Use a sentinel value so a first emit of undefined doesn't early exit below
        let previousValue: T | object = new Object();
        this.subscription = value$.subscribe((v) => {
            this.isLoading = false;

            if (previousValue === v) {
                return;
            }

            this.viewContainer.clear();
            this.viewContainer.createEmbeddedView(this.templateRef, {
                $implicit: v,
            });
            this.cdr.markForCheck();

            previousValue = v;
        });
    }

    static ngTemplateContextGuard<T>(
        _dir: LoadingUntilFirstEmitDirective<T>,
        ctx: unknown,
    ): ctx is { $implicit: T } {
        return true;
    }
}
